import moment from 'moment'
import { minhaCarteira } from '@constants/URL'
import { formatQueryParams } from '@utils/Format'
import { UNTIL_LAST_MONTH_FILTER } from '@modules/consolidator/utils/string-build/build-asset-statistics-url'

export const buildStatisticsUrl = (requestArgs = {}) => {
  const baseUrl = buildBaseUrl(requestArgs)
  const statisticsQueryParams = buildWalletRequestParameters(requestArgs)
  const normalizedParams = normalizeInvestmentTypeGroupingParams(
    statisticsQueryParams,
    requestArgs
  )

  return baseUrl + formatQueryParams(normalizedParams)
}

export const buildWalletQueryParams = ({
  groupBy,
  modoFiltroCorretora,
  filtroCorretora,
  investmentTypeFilter,
  showCustomDate,
  filtroData,
  OPCOES_FILTRO_DATA,
  dataInicio,
  dataFim,
  extraQueryParams,
}) => {
  return buildWalletRequestParameters({
    groupBy,
    modoFiltroCorretora,
    filtroCorretora,
    investmentTypeFilter,
    showCustomDate,
    filtroData,
    OPCOES_FILTRO_DATA,
    dataInicio,
    dataFim,
    extraQueryParams,
  })
}

export const buildWalletRequestParameters = (requestArgs = {}) => {
  const baseParameters = buildBaseWalletParams(requestArgs)
  const parametersWithExtra = applyExtraParameters(baseParameters, requestArgs)
  const parametersWithDate = applyDateParameters(
    parametersWithExtra,
    requestArgs
  )

  return parametersWithDate
}

const normalizeInvestmentTypeGroupingParams = (
  params = {},
  requestArgs = {}
) => {
  if (requestArgs.isClient) {
    return params
  }

  if (params.group_by !== 'tipo_investimento') {
    return params
  }

  const normalizedType = formatInvestmentTypeFilter(
    params.tipo_investimento || ''
  )

  if (!normalizedType) {
    return params
  }

  const updatedParams = { ...params, corretora: normalizedType }
  delete updatedParams.tipo_investimento

  return updatedParams
}

export const formatInvestmentTypeFilter = (investmentTypeFilter) => {
  const normalizedValue = String(investmentTypeFilter || '')
    .trim()
    .toLowerCase()

  if (!normalizedValue) {
    return null
  }

  const validTypes = ['offshore', 'onshore']
  const matchedType = validTypes.find((type) => normalizedValue.includes(type))

  return matchedType || null
}

export const formatWalletEndDate = (isoDate) => {
  if (!isoDate) {
    return null
  }

  return moment(isoDate, 'YYYY-MM-DD').format('DD-MM-YYYY')
}

const buildBaseWalletParams = (requestArgs) => {
  const {
    group,
    groupBy,
    withGraph,
    modoFiltroCorretora,
    filtroCorretora,
    investmentTypeFilter,
  } = requestArgs

  const normalizedGroupBy = group || groupBy
  const baseParams = {}

  if (normalizedGroupBy) {
    baseParams.group_by = normalizedGroupBy
  }

  if (typeof withGraph === 'boolean') {
    baseParams.with_graph = withGraph
  }

  if (modoFiltroCorretora) {
    const filterValue = computeFilterValue({ filtroCorretora })
    baseParams[modoFiltroCorretora] = filterValue
  }

  const formattedInvestmentType =
    formatInvestmentTypeFilter(investmentTypeFilter)

  if (formattedInvestmentType) {
    baseParams.tipo_investimento = formattedInvestmentType
  }

  return baseParams
}

const applyExtraParameters = (baseParams, requestArgs) => {
  const { extraQueryParams } = requestArgs

  if (!extraQueryParams || typeof extraQueryParams !== 'object') {
    return baseParams
  }

  return {
    ...baseParams,
    ...extraQueryParams,
  }
}

const applyDateParameters = (currentParams, requestArgs) => {
  const {
    showCustomDate,
    filtroData,
    OPCOES_FILTRO_DATA,
    dataInicio,
    dataFim,
  } = requestArgs

  const shouldApplyCustomRange = shouldUseCustomRange({
    showCustomDate,
    filtroData,
  })

  if (shouldApplyCustomRange) {
    return applyCustomRangeParameters(currentParams, { dataInicio, dataFim })
  }

  if (filtroData === UNTIL_LAST_MONTH_FILTER) {
    return {
      ...currentParams,
      end_date: moment().subtract(1, 'month').endOf('month').format('DD-MM-YYYY'),
    }
  }

  if (!filtroData || !OPCOES_FILTRO_DATA) {
    return currentParams
  }

  const presetBeginDate = computePresetBeginDate({
    filtroData,
    OPCOES_FILTRO_DATA,
  })

  if (!presetBeginDate) {
    return currentParams
  }

  return {
    ...currentParams,
    begin_date: presetBeginDate,
  }
}

const applyCustomRangeParameters = (currentParams, dateArguments) => {
  const { dataInicio, dataFim } = dateArguments
  const { beginDate, endDate } = buildCustomRangeDates({
    dataInicio,
    dataFim,
  })

  if (!beginDate && !endDate) {
    return currentParams
  }

  const updatedParams = { ...currentParams }

  if (beginDate) {
    updatedParams.begin_date = beginDate
  }

  if (endDate) {
    updatedParams.end_date = endDate
  }

  return updatedParams
}

const buildBaseUrl = ({ isClient, currentClientId }) => {
  if (isClient) {
    return minhaCarteira.cliente.estatisticas_benchmark
  }

  return minhaCarteira.estatisticas_benchmark + (currentClientId || '')
}

const computeFilterValue = ({ filtroCorretora }) => {
  if (filtroCorretora === 'Consolidado') {
    return 'Todos'
  }

  if (!filtroCorretora) {
    return 'Todos'
  }

  return filtroCorretora
}

const shouldUseCustomRange = ({ showCustomDate, filtroData }) => {
  return Boolean(showCustomRangeFlag({ showCustomDate, filtroData }))
}

const showCustomRangeFlag = ({ showCustomDate, filtroData }) => {
  if (showCustomDate) {
    return true
  }

  return filtroData === 'Específica'
}

const buildCustomRangeDates = ({ dataInicio, dataFim }) => {
  const beginDate = dataInicio ? formatWalletEndDate(dataInicio) : null
  const endDate = dataFim ? formatWalletEndDate(dataFim) : null

  return { beginDate, endDate }
}

const computePresetBeginDate = ({ filtroData, OPCOES_FILTRO_DATA }) => {
  const filterOptions = OPCOES_FILTRO_DATA

  if (filtroData === filterOptions.INICIO) {
    return ''
  }

  if (filtroData === filterOptions.SEMANA) {
    return moment().startOf('week').format('DD-MM-YYYY')
  }

  if (filtroData === filterOptions.MES) {
    return moment().startOf('month').format('DD-MM-YYYY')
  }

  if (filtroData === filterOptions.SEMESTRE) {
    return moment().subtract(6, 'month').format('DD-MM-YYYY')
    // .startOf('month')
    // Conforme comentado abaixo, alteramos a logica para basicamente considerar
    // 6 meses corridos
  }

  if (filtroData === filterOptions.YTD) {
    return moment().startOf('year').format('DD-MM-YYYY')
  }

  if (filtroData === filterOptions.ANO) {
    return moment().subtract(12, 'month').format('DD-MM-YYYY')
    // .startOf('month')
    // John solicitou para remover essa questao de jogar para o inicio do mes
    // Basicamente alterando a logica do 12 meses para realmente calcular os ultimos 12 meses corridos
  }

  return ''
}
